import { useMemo } from 'react';
import { useGetEventSettingsQuery } from '@/state/queries/events/use-get-event-settings';
import { SESSION_TYPE, type SessionType } from '@/types/dtos/sessions/session-type.enum';

/** Session types that may be selected when creating a session (offered + commercially entitled). */
export function useOfferedSessionTypes(eventId?: string) {
  const { data, isLoading } = useGetEventSettingsQuery(eventId);
  const hasSessionTypesConfig = Boolean(data?.sessionTypes);

  const offeredTypes = useMemo(() => {
    const catalog = data?.sessionTypes?.catalog;
    if (catalog?.length) {
      return catalog
        .filter((entry) => entry.isEntitled && entry.effective.offered)
        .map((entry) => entry.type as SessionType);
    }
    const byType = data?.sessionTypes?.byType;
    if (!byType) {
      return Object.values(SESSION_TYPE) as SessionType[];
    }
    return (Object.entries(byType) as [SessionType, { offered: boolean }][])
      .filter(([, policy]) => policy.offered)
      .map(([type]) => type);
  }, [data?.sessionTypes]);

  /** Use in create-session UIs. Falls back to all types only while settings load or lack sessionTypes. */
  const selectableSessionTypes = useMemo((): SessionType[] => {
    if (isLoading || !hasSessionTypesConfig) {
      return Object.values(SESSION_TYPE) as SessionType[];
    }
    return offeredTypes;
  }, [isLoading, hasSessionTypesConfig, offeredTypes]);

  return { offeredTypes, selectableSessionTypes, isLoading, eventSettings: data };
}
